import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ScanLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";
import AnalyzingOverlay from "@/components/AnalyzingOverlay";

export default function Analyze() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualText, setManualText] = useState("");

  useEffect(() => {
    if (!id) return;
    api.get(`/businesses/${id}`).then((r) => setBiz(r.data));
  }, [id]);

  const start = async (withManual = false) => {
    setLoading(true);
    setSuccess(false);
    try {
      const payload = { business_id: id };
      if (withManual && manualText.trim()) payload.manual_text = manualText.trim();

      // POST /analyze now returns immediately with a run_id; the actual scan
      // runs in the background. We poll /analysis-runs/{id} until it
      // finishes — this lets analyses safely take > 60s without tripping
      // the K8s ingress proxy timeout.
      const startRes = await api.post("/analyze", payload);
      const runId = startRes?.data?.run_id;
      if (!runId) throw new Error("missing run_id");

      // Poll every 2s, up to ~3.5 min cap.
      const maxAttempts = 105;
      let attempt = 0;
      let finalStatus = "running";
      let lastError = "";
      while (attempt < maxAttempts) {
        attempt += 1;
        await new Promise((r) => setTimeout(r, 2000));
        const statusRes = await api.get(`/analysis-runs/${runId}`);
        finalStatus = statusRes?.data?.status;
        if (finalStatus === "done") break;
        if (finalStatus === "error") {
          lastError = statusRes?.data?.error || "";
          break;
        }
      }

      if (finalStatus !== "done") {
        throw new Error(lastError || t("analyze.error"));
      }

      toast.success(t("analyze.success"));
      setSuccess(true);
      setTimeout(() => navigate(`/app/isletme/${id}`), 1600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || t("analyze.error"));
      setLoading(false);
      setSuccess(false);
    }
  };

  if (!biz) return <div className="text-slate-400 flex items-center"><Loader2 className="animate-spin mr-2" /> {t("common.loading")}</div>;

  return (
    <div className="space-y-6 pb-60 sm:pb-6" data-testid="analyze-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">{t("analyze.eyebrow")}</div>
        <h1 className="font-display text-4xl font-bold text-white">{t("analyze.title", { name: biz.name })}</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">{t("analyze.subtitle")}</p>
      </div>

      <div className="glass-strong rounded-3xl p-7 space-y-4">
        <Row label={t("common.business")}>{biz.name}</Row>
        <Row label={t("common.sector")}>{biz.sector}</Row>
        <Row label={t("common.city")}>{biz.city}</Row>
        <Row label={t("common.website")}>{biz.website_url || <span className="text-slate-500">{t("common.none")}</span>}</Row>
        <Row label={t("common.competitors")}>
          {(biz.competitors || []).length === 0 ? <span className="text-slate-500">{t("common.none")}</span> : (
            <ul className="space-y-1">
              {biz.competitors.map((c, i) => <li key={i} className="text-slate-300 text-sm">{c}</li>)}
            </ul>
          )}
        </Row>
      </div>

      {!showManual ? (
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => start(false)}
            disabled={loading}
            className="neon-btn rounded-xl px-7 py-3.5 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="analyze-start"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
            {t("analyze.startBtn")}
          </button>
          <button
            onClick={() => setShowManual(true)}
            className="ghost-btn rounded-xl px-5 py-3 text-sm text-white"
            data-testid="analyze-manual-toggle"
          >
            {t("analyze.manualBtn")}
          </button>
        </div>
      ) : (
        <div className="glass-strong rounded-3xl p-6 space-y-3">
          <div className="flex items-start gap-2 text-amber-300 text-sm">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            {t("analyze.manualHint")}
          </div>
          <textarea
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            className="w-full min-h-[200px] bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm focus:outline-none focus:border-blue-500"
            placeholder={t("analyze.manualPh")}
            data-testid="analyze-manual-text"
          />
          <div className="flex gap-3">
            <button
              onClick={() => start(true)}
              disabled={loading || !manualText.trim()}
              className="neon-btn rounded-xl px-6 py-3 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="analyze-manual-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
              {t("analyze.submitManual")}
            </button>
            <button onClick={() => setShowManual(false)} className="ghost-btn rounded-xl px-5 py-3 text-sm text-white">{t("analyze.cancel")}</button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-sm text-slate-400">{t("analyze.running")}</div>
      )}

      <AnalyzingOverlay open={loading} success={success} />
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6">
      <div className="text-xs uppercase tracking-wider text-slate-500 sm:w-32">{label}</div>
      <div className="text-white">{children}</div>
    </div>
  );
}
